/**
 * GDPR ẩn danh nhất quán (người-rời) + bằng-chứng-là-CLAIM (US-E19.F5-0204 · @UC9312 @UC9313 @UC9314).
 * 🔴 @gate pii + money.
 *
 * THUẦN: không DB, không I/O, KHÔNG `Date.now()`, KHÔNG `Math.random()` — cùng input ⇒ cùng output.
 *
 * 🔴 HAI BẤT BIẾN CỦA TASK, ĐẶT Ở ĐÂY (1 NGUỒN SỰ THẬT):
 *
 * (1) GDPR = **ẨN DANH, KHÔNG XOÁ SỔ** (P7.3). Sau khi ẩn danh, mọi bút toán/nợ mà người đó có phần
 *     VẪN CÒN NGUYÊN (append-only), chỉ danh tính bị cắt. Chỗ nào render tên người đã ẩn danh thì
 *     hiện `ANONYMIZED_MEMBER_DISPLAY_NAME` — KHÔNG rơi về email/uuid/null (null ⇒ FE dễ crash hoặc
 *     rơi về fallback lộ email cũ). Đây là nhãn hiển thị DUY NHẤT cho người đã rời + đã ẩn danh.
 *
 * (2) EVIDENCE = **CLAIM, KHÔNG PHẢI FACT** (P7.3 / SEC-FAKE-PROOF). Bằng chứng người dùng nộp (ảnh
 *     bill, ảnh xác nhận CK) mặc định `unverified`. Chuyển sang `valid`/`invalid` là hành động CÓ CHỦ
 *     THỂ (owner/treasurer soát — có `verifiedBy` + `verifiedAt`). REUSE nguyên xi bộ enum
 *     `VERIFICATION_STATUS` của E19.F4 (`compliance-tax-kyc.ts`) — **KHÔNG dựng bộ từ vựng thứ hai**
 *     ("claim" chỉ là CÁCH GỌI của `unverified`; thêm giá trị 'claim' vào DB = 2 whitelist lệch nhau).
 */

import { VERIFICATION_STATUS, type VerificationStatus } from "./compliance-tax-kyc.js";

/* ─────────────────────── (1) GDPR — nhãn hiển thị người đã ẩn danh ─────────────────────── */

/**
 * 🔴 Nhãn hiển thị của thành viên đã rời + đã ẩn danh (AC2). Bút toán/nợ của họ VẪN hiện trong lịch
 * sử (không được xoá — append-only), nhưng danh tính thì KHÔNG: không email cũ, không tên thật,
 * không SĐT. Hằng số này là NHÃN DUY NHẤT — mọi nơi render tên phải dùng nó (không tự chế chuỗi khác).
 */
export const ANONYMIZED_MEMBER_DISPLAY_NAME = "Thành viên đã rời" as const;

/** Trạng thái `users.status` sau khi ẩn danh (khớp `delete-me.service.ts` — 1 nguồn sự thật). */
export const ANONYMIZED_USER_STATUS = "anonymized" as const;

/**
 * Nguồn tối thiểu để quyết định nhãn hiển thị của 1 membership. `userStatus` = `users.status`;
 * `displayName` = `group_members.displayName` (nickname per-group — CŨNG LÀ PII, phải null sau ẩn danh).
 */
export interface MemberDisplaySource {
  readonly userStatus: string | null;
  readonly displayName: string | null;
  readonly email: string | null;
}

/**
 * Nhãn hiển thị của 1 membership (AC2 — thuần, deterministic).
 *
 * 🔴 FAIL-CLOSED VỀ PII: user đã `anonymized` ⇒ LUÔN `ANONYMIZED_MEMBER_DISPLAY_NAME`, **KỂ CẢ KHI**
 * `displayName`/`email` chưa được null-hoá (dữ liệu cũ / purge nửa vời). Nghĩa là: kể cả nếu ai đó
 * quên xoá 1 cột PII, nhãn hiển thị VẪN không rò tên/email cũ. Đảo thứ tự (đọc displayName trước
 * status) ⇒ PII rò ⇒ test ĐỎ.
 *
 * Người CÒN hoạt động: giữ nguyên hành vi cũ (displayName → prefix email → "Thành viên") ⇒ 0 regress.
 */
export function resolveMemberDisplayName(source: MemberDisplaySource): string {
  if (source.userStatus === ANONYMIZED_USER_STATUS) {
    return ANONYMIZED_MEMBER_DISPLAY_NAME;
  }
  const nickname = source.displayName?.trim();
  if (nickname) return nickname;
  const email = source.email ?? "";
  const at = email.indexOf("@");
  if (at > 0) return email.slice(0, at);
  return "Thành viên";
}

/* ─────────────────────── (2) Evidence = CLAIM (không tin mù) ─────────────────────── */

/**
 * 🔴 Trạng thái xác minh của bằng chứng MỚI nộp = `unverified` (= "claim"). REUSE
 * `DEFAULT_VERIFICATION_STATUS` của E19.F4 nhưng đặt tên theo ngữ cảnh evidence để đọc code rõ nghĩa.
 * Chỉ 1 giá trị, 1 whitelist DB — không có enum 'claim' thứ hai.
 */
export const EVIDENCE_CLAIM_STATUS: VerificationStatus = VERIFICATION_STATUS.UNVERIFIED;

/** Nhãn hiển thị cho FE khi bằng chứng chưa được ai soát (AC1 — "chưa xác minh"). */
export const EVIDENCE_UNVERIFIED_LABEL = "Chưa xác minh" as const;

/**
 * Trạng thái mà một hành động SOÁT được phép đặt: `valid` | `invalid`. **KHÔNG** cho phép quay về
 * `unverified` (xoá dấu vết soát) và KHÔNG có giá trị nào khác.
 */
export const EVIDENCE_DECIDABLE_STATUSES = [
  VERIFICATION_STATUS.VALID,
  VERIFICATION_STATUS.INVALID,
] as const;
export type EvidenceDecision = (typeof EVIDENCE_DECIDABLE_STATUSES)[number];

/** true nếu `value` là quyết định soát hợp lệ (valid|invalid). Fail-closed cho mọi giá trị lạ. */
export function isEvidenceDecision(value: unknown): value is EvidenceDecision {
  return (
    value === VERIFICATION_STATUS.VALID || value === VERIFICATION_STATUS.INVALID
  );
}

/**
 * 🔴 KHÔNG TIN MÙ (AC4/AC5): bằng chứng CHỈ được coi là căn cứ đổi trạng thái tiền khi đã có người
 * soát và kết luận `valid`. `unverified` (claim) và `invalid` ⇒ **false** ⇒ tầng gọi KHÔNG được post/
 * reverse bút toán dựa trên nó.
 *
 * Mutation đổi `=== VALID` thành `!== INVALID` (tin luôn claim) ⇒ test AC4 ĐỎ.
 */
export function isEvidenceTrustworthy(status: string | null | undefined): boolean {
  return status === VERIFICATION_STATUS.VALID;
}

/**
 * Kết luận dispute có được phép DỰA VÀO bằng chứng để đổi tiền hay không.
 *
 * Trả `true` CHỈ KHI có ÍT NHẤT MỘT bằng chứng đã soát `valid`. Tập rỗng ⇒ false. Toàn `unverified`
 * ⇒ false (claim-mù KHÔNG đủ). Toàn `invalid` ⇒ false. Fail-closed.
 */
export function canSettleFromEvidence(
  statuses: readonly (string | null | undefined)[],
): boolean {
  return statuses.some((s) => isEvidenceTrustworthy(s));
}
